import { useEffect, useId, useState } from "react";
import { Sliders } from "lucide-react";
import { InstanceSettings, type InstanceSettingsResponse } from "@spf/contracts";
import { Card } from "../Card.js";
import { api } from "../../lib/client.js";
import { buttonClass } from "../ViewState.js";

interface Props {
  /** The floor as the server currently holds it, or null while unknown. */
  current: number | null;
  canWrite: boolean;
}

/**
 * The design's emergency-reserve slider, wired to the real setting.
 *
 * In the design this control is local state that resets on every navigation
 * and reaches nothing. Here it writes `batteryReserveSocPct` through the same
 * authenticated `PUT /api/v1/settings` the Settings view uses, and the number
 * it sets is the floor the server measures runtime down to -- so moving it
 * changes the answer on this page rather than decorating it.
 *
 * **It changes how this system reports, never how the inverter behaves.**
 * `DECISIONS/0004` rules out writing to the hardware, and a control that
 * looked like it was reserving capacity in the battery itself would be
 * claiming exactly that. The wording below says which one it is.
 */
export function ReserveCard({ current, canWrite }: Props) {
  const id = useId();
  const [value, setValue] = useState<number | null>(current);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // The server's value wins whenever it changes underneath, which it does on
  // every reconnect and after anyone else saves.
  useEffect(() => {
    setValue(current);
  }, [current]);

  async function save(next: number): Promise<void> {
    setProblem(null);
    setSaving(true);
    try {
      const settings: InstanceSettingsResponse = await api.settings();
      const merged = InstanceSettings.parse({ ...settings.values, batteryReserveSocPct: next });
      const stored = await api.saveSettings(merged);
      setValue(stored.values.batteryReserveSocPct);
      setSaved(true);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Could not save the reserve floor.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Emergency reserve floor">
      <p className="mb-6 flex items-start gap-2 text-xs leading-relaxed text-ink-muted">
        <Sliders size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-accent-ink" />
        <span>
          How far down the bank counts as empty. Runtime on this page is measured to here rather
          than to zero. It changes what this dashboard reports, not what the inverter does.
        </span>
      </p>

      {value === null ? (
        // No form here, and so no live regions either: an always-mounted
        // `role="alert"` on a card with nothing to report is a second alert on
        // a page that already has one, and a reader looking for the real
        // message finds two.
        <p className="text-sm text-ink-muted">Not configured yet.</p>
      ) : (
        <div className="space-y-3">
          <div className="tabular flex items-center justify-between">
            <label htmlFor={id} className="text-sm text-ink-muted">
              Threshold
            </label>
            <output htmlFor={id} className="text-xl font-bold text-danger-ink">
              {`${String(value)}%`}
            </output>
          </div>
          <input
            id={id}
            type="range"
            min={0}
            max={50}
            step={5}
            value={value}
            disabled={!canWrite || saving}
            onChange={(event) => {
              setSaved(false);
              setValue(Number(event.target.value));
            }}
            className="w-full accent-[var(--danger)] disabled:opacity-40"
          />
          <div className="tabular flex justify-between text-[11px] text-ink-muted">
            <span>0%</span>
            <span>25%</span>
            <span>50%</span>
          </div>

          {canWrite ? (
            <button
              type="button"
              className={buttonClass}
              disabled={saving || value === current}
              onClick={() => {
                void save(value);
              }}
            >
              {saving ? "Saving…" : "Save reserve floor"}
            </button>
          ) : (
            <p className="text-xs text-ink-muted">Changing this needs an administrator.</p>
          )}

          <p role="status" className="mt-3 text-xs text-ink-muted">
            {saved ? "Reserve floor saved." : ""}
          </p>
          <p role="alert" className="text-xs text-danger-ink">
            {problem ?? ""}
          </p>
        </div>
      )}
    </Card>
  );
}
